import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Users, Search, BookOpen, CheckCircle2, Calendar, Award, Download, UserPlus, Ban, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function CourseStudents({ user }) {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingCompletion, setUpdatingCompletion] = useState(null);
  const [updatingRestriction, setUpdatingRestriction] = useState(null);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [enrollSearchQuery, setEnrollSearchQuery] = useState("");
  const [enrollingStudent, setEnrollingStudent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadStudents();
  }, [courseId]);

  useEffect(() => {
    if (showEnrollDialog) {
      loadAllStudents();
    }
  }, [showEnrollDialog]);

  const loadAllStudents = async () => {
    try {
      const response = await fetch(`${API}/students`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const students = await response.json();
        // Filter to only show students not already enrolled
        const enrolledUserIds = data?.students?.map(s => s.user_id) || [];
        const availableStudents = students.filter(s => !enrolledUserIds.includes(s.user_id));
        setAllStudents(availableStudents);
      }
    } catch (error) {
      console.error('Failed to load students');
    }
  };

  const handleEnrollStudent = async (studentId) => {
    setEnrollingStudent(studentId);
    try {
      const response = await fetch(`${API}/courses/${courseId}/enroll-student`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: studentId })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowEnrollDialog(false);
        loadStudents(); // Reload the enrolled students list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to enroll student');
      }
    } catch (error) {
      toast.error('Failed to enroll student');
    } finally {
      setEnrollingStudent(null);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/students`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setData(data);
      } else {
        toast.error(data.detail || 'Failed to load students');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletionToggle = async (studentId, currentlyCompleted) => {
    setUpdatingCompletion(studentId);
    
    try {
      const response = await fetch(`${API}/courses/${courseId}/students/${studentId}/completion`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: studentId,
          completed: !currentlyCompleted
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success(result.message);
        // Reload students to get updated data
        await loadStudents();
      } else {
        toast.error(result.detail || 'Failed to update completion status');
      }
    } catch (error) {
      toast.error('Failed to update completion status');
    } finally {
      setUpdatingCompletion(null);
    }
  };

  const handleRestrictionToggle = async (studentId, currentlyRestricted) => {
    setUpdatingRestriction(studentId);
    try {
      const response = await fetch(`${API}/courses/${courseId}/students/${studentId}/restriction`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ restricted: !currentlyRestricted })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(result.message);
        await loadStudents();
      } else {
        toast.error(result.detail || 'Failed to update restriction');
      }
    } catch (error) {
      toast.error('Failed to update restriction');
    } finally {
      setUpdatingRestriction(null);
    }
  };

  const handleDownloadCertificate = async (certificateId, studentName, courseName) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/certificates/${certificateId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate_${studentName.replace(/\s+/g, '_')}_${courseName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Certificate downloaded');
      } else {
        toast.error('Failed to download certificate');
      }
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  const filteredStudents = data?.students?.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const completedCount = data?.students?.filter(s => s.is_completed).length || 0;

  if (user?.role !== 'teacher' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Only teachers can view enrolled students</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading students...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/courses/${courseId}`)}
            data-testid="back-to-course-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Course</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-4 sm:p-6 md:p-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-[#3A5A40]" strokeWidth={1.5} />
              <h1 className="text-2xl sm:text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                Enrolled Students
              </h1>
            </div>
            <p className="text-base sm:text-lg text-[#588157]">{data?.course?.title}</p>
          </div>
          
          {/* Enroll Student Button */}
          <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md"
                data-testid="enroll-student-button"
              >
                <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Enroll Student
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-[#FEFAE0] border-[#CCD5AE]">
              <DialogHeader>
                <DialogTitle className="text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                  Enroll a Student
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3B18A]" />
                  <Input
                    placeholder="Search students by name or email..."
                    value={enrollSearchQuery}
                    onChange={(e) => setEnrollSearchQuery(e.target.value)}
                    className="pl-9 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                    data-testid="enroll-search-input"
                  />
                </div>
                
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {allStudents
                    .filter(s => 
                      s.name?.toLowerCase().includes(enrollSearchQuery.toLowerCase()) ||
                      s.email?.toLowerCase().includes(enrollSearchQuery.toLowerCase())
                    )
                    .map((student) => (
                      <div
                        key={student.user_id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#CCD5AE]/50 hover:border-[#3A5A40]/30"
                      >
                        <div className="flex items-center gap-3">
                          {student.picture ? (
                            <img src={student.picture} alt={student.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#3A5A40]/10 flex items-center justify-center">
                              <span className="text-sm font-medium text-[#3A5A40]">
                                {student.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-[#344E41]">{student.name}</p>
                            <p className="text-xs text-[#A3B18A]">{student.email}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleEnrollStudent(student.user_id)}
                          disabled={enrollingStudent === student.user_id}
                          className="bg-[#3A5A40] text-white hover:bg-[#344E41] text-xs"
                          data-testid={`enroll-btn-${student.user_id}`}
                        >
                          {enrollingStudent === student.user_id ? 'Enrolling...' : 'Enroll'}
                        </Button>
                      </div>
                    ))}
                  
                  {allStudents.filter(s => 
                    s.name?.toLowerCase().includes(enrollSearchQuery.toLowerCase()) ||
                    s.email?.toLowerCase().includes(enrollSearchQuery.toLowerCase())
                  ).length === 0 && (
                    <p className="text-center text-sm text-[#A3B18A] py-4">
                      {allStudents.length === 0 
                        ? "All students are already enrolled in this course"
                        : "No students match your search"}
                    </p>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-[#3A5A40]/10 rounded-xl">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-[#A3B18A]">Total Students</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#344E41]">{data?.total_students || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-[#588157]/10 rounded-xl">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-[#588157]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-[#A3B18A]">Completed</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#344E41]">{completedCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-[#D4A373]/10 rounded-xl">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4A373]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-[#A3B18A]">Lessons</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#344E41]">{data?.course?.content?.length || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-[#588157]/10 rounded-xl">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#588157]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-[#A3B18A]">Duration</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#344E41]">{data?.course?.duration_weeks || 0}w</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A3B18A]" />
            <Input
              placeholder="Search students by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-students-input"
              className="pl-10 bg-[#FEFAE0] border-[#CCD5AE] focus:border-[#3A5A40] rounded-lg"
            />
          </div>
        </div>

        {/* Students List */}
        {filteredStudents.length === 0 ? (
          <Card className="p-8 sm:p-12 bg-[#FEFAE0] border-[#CCD5AE]/50 text-center rounded-2xl">
            <Users className="w-12 h-12 sm:w-16 sm:h-16 text-[#CCD5AE] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-lg sm:text-xl text-[#588157] mb-2">No students enrolled yet</p>
            <p className="text-sm sm:text-base text-[#A3B18A]">Students will appear here once they enroll in this course.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredStudents.map((student) => (
              <Card
                key={student.user_id}
                className={`p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl hover:shadow-md transition-shadow ${student.is_completed ? 'ring-2 ring-[#588157]/30' : ''}`}
                data-testid={`student-card-${student.user_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Completion Checkbox + Avatar */}
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex items-center">
                      <Checkbox
                        id={`complete-${student.user_id}`}
                        checked={student.is_completed}
                        onCheckedChange={() => handleCompletionToggle(student.user_id, student.is_completed)}
                        disabled={updatingCompletion === student.user_id}
                        className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-[#3A5A40] data-[state=checked]:bg-[#3A5A40] data-[state=checked]:border-[#3A5A40]"
                        data-testid={`completion-checkbox-${student.user_id}`}
                      />
                    </div>
                    
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {student.picture ? (
                        <img
                          src={student.picture}
                          alt={student.name}
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#CCD5AE]"
                        />
                      ) : (
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#3A5A40]/10 flex items-center justify-center">
                          <span className="text-lg sm:text-xl font-bold text-[#3A5A40]">
                            {student.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Student Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base sm:text-lg font-semibold text-[#344E41] truncate">{student.name}</h3>
                        {student.is_completed && (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-[#588157]/10 text-[#588157] text-xs font-medium rounded-full flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            Completed
                          </span>
                        )}
                        {student.is_restricted && (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1" data-testid={`restricted-badge-${student.user_id}`}>
                            <Ban className="w-3 h-3" />
                            Restricted
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-[#588157] truncate">{student.email}</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {student.enrolled_at && (
                          <p className="text-xs text-[#A3B18A]">
                            Enrolled: {new Date(student.enrolled_at).toLocaleDateString()}
                          </p>
                        )}
                        {student.completed_at && (
                          <p className="text-xs text-[#588157] font-medium">
                            • Completed: {new Date(student.completed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress + Actions */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 ml-8 sm:ml-0">
                    {/* Progress Indicators */}
                    <div className="flex gap-4 sm:gap-6">
                      {/* Lesson Progress */}
                      <div className="text-center">
                        <div className="flex items-center gap-1 mb-1">
                          <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-[#588157]" />
                          <span className="text-xs sm:text-sm text-[#588157]">Lessons</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-16 sm:w-24 bg-[#E9EDC9] rounded-full h-2">
                            <div
                              className="bg-[#588157] h-2 rounded-full transition-all"
                              style={{ width: `${student.lesson_progress}%` }}
                            />
                          </div>
                          <span className="text-xs sm:text-sm font-medium text-[#344E41]">
                            {student.completed_lessons}/{student.total_lessons}
                          </span>
                        </div>
                      </div>

                      {/* Activity Progress */}
                      {student.total_activities > 0 && (
                        <div className="text-center">
                          <div className="flex items-center gap-1 mb-1">
                            <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-[#D4A373]" />
                            <span className="text-xs sm:text-sm text-[#D4A373]">Activities</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-16 sm:w-24 bg-[#E9EDC9] rounded-full h-2">
                              <div
                                className="bg-[#D4A373] h-2 rounded-full transition-all"
                                style={{ width: `${student.activity_progress}%` }}
                              />
                            </div>
                            <span className="text-xs sm:text-sm font-medium text-[#344E41]">
                              {student.completed_activities}/{student.total_activities}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Restrict/Unrestrict Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestrictionToggle(student.user_id, student.is_restricted)}
                      disabled={updatingRestriction === student.user_id}
                      title={student.is_restricted
                        ? "Re-enable email notifications, discussions and chat history for this student"
                        : "Disable email notifications, discussions and chat history for this student"}
                      className={student.is_restricted
                        ? "border-[#588157] text-[#588157] hover:bg-[#588157]/10 rounded-lg text-xs sm:text-sm"
                        : "border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-xs sm:text-sm"}
                      data-testid={`restrict-toggle-${student.user_id}`}
                    >
                      {student.is_restricted ? (
                        <>
                          <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4 mr-1" strokeWidth={1.5} />
                          {updatingRestriction === student.user_id ? 'Updating...' : 'Unrestrict'}
                        </>
                      ) : (
                        <>
                          <Ban className="w-3 h-3 sm:w-4 sm:h-4 mr-1" strokeWidth={1.5} />
                          {updatingRestriction === student.user_id ? 'Updating...' : 'Restrict'}
                        </>
                      )}
                    </Button>

                    {/* Download Certificate Button */}
                    {student.is_completed && student.certificate_id && (
                      <Button
                        size="sm"
                        onClick={() => handleDownloadCertificate(student.certificate_id, student.name, data?.course?.title)}
                        className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg text-xs sm:text-sm"
                        data-testid={`download-cert-${student.user_id}`}
                      >
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" strokeWidth={1.5} />
                        Certificate
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
